import { ref } from 'vue'

// On-screen log panel focused on the bridge/swap flow (tx dispatch, wallet
// requests, approve/send hashes, polling). Token-discovery spam (Multicall,
// balance fetch) is intentionally ignored — it's noise for flow debugging.
const DEBUG_LOG_PATTERNS = [
  '[BridgeExecution]',
  '[BridgePolling]',
  '[EvmWallet]',
  '[SolanaWallet]',
  '[TonWallet]',
  '[CrosschainBuy]',
  '[DestinationSwap]',
  '[TokenSelection]',
]

const shouldLogToPanel = (msg: string): boolean =>
  DEBUG_LOG_PATTERNS.some((prefix) => msg.includes(prefix))

const stringifyArg = (a: unknown): string => {
  if (a instanceof Error) return a.message
  if (typeof a === 'object' && a !== null) {
    try {
      return JSON.stringify(a)
    } catch {
      return '[unserializable object]'
    }
  }
  return String(a)
}

export function useDebugLogPanel() {
  const debugLog = ref<string[]>([])
  const debugCopyStatus = ref('')

  const pushDebug = (level: 'LOG' | 'WARN' | 'ERR', args: unknown[]) => {
    const msg = args.map(stringifyArg).join(' ')
    if (!shouldLogToPanel(msg)) return
    debugLog.value.push(`${level} ${new Date().toLocaleTimeString()}: ${msg.slice(0, 400)}`)
    // Cap at last 200 lines to keep the panel responsive.
    if (debugLog.value.length > 200) debugLog.value.splice(0, debugLog.value.length - 200)
  }

  const copyDebugLog = async () => {
    const text = debugLog.value.join('\n')
    // navigator.clipboard requires secure context (https/localhost). On local
    // network IPs (192.168.x) it's often unavailable, so fall back to a hidden
    // textarea + execCommand.
    try {
      if ((navigator.clipboard as Clipboard | undefined)?.writeText) {
        await navigator.clipboard.writeText(text)
        debugCopyStatus.value = 'copied'
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        // eslint-disable-next-line @typescript-eslint/no-deprecated, sonarjs/deprecation
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        debugCopyStatus.value = ok ? 'copied' : 'failed'
      }
    } catch {
      debugCopyStatus.value = 'failed'
    }
    setTimeout(() => {
      debugCopyStatus.value = ''
    }, 1500)
  }

  // Intentional console-method overrides for in-app debug panel mirroring.
  // The lint rule forbids console.log calls, but here we're overriding the
  // method itself — disabling no-console at function scope.
  /* eslint-disable no-console */
  const installConsoleTaps = () => {
    const _origConsoleError = console.error
    const _origConsoleLog = console.log
    const _origConsoleWarn = console.warn
    console.error = (...args: unknown[]) => {
      _origConsoleError(...args)
      pushDebug('ERR', args)
    }
    console.log = (...args: unknown[]) => {
      _origConsoleLog(...args)
      pushDebug('LOG', args)
    }
    // createLogger() in services/crosschain/logger.ts routes .log/.warn through
    // console.warn, so we must tap warn too.
    console.warn = (...args: unknown[]) => {
      _origConsoleWarn(...args)
      pushDebug('WARN', args)
    }
  }
  /* eslint-enable no-console */

  installConsoleTaps()

  return { debugLog, debugCopyStatus, copyDebugLog }
}
